// NodeCoverField.jsx

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

// Utilities
import { createObjectUrl, isBlobUrl, revokeObjectUrl } from 'utilities/Images';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './NodeCoverField.css';

// Icons
import Icon from 'svg/Icon';

// Components
import CoverActionButton from 'node/form/fields/cover/actionbutton/CoverActionButton';
import NodeCover from 'node/form/fields/cover/NodeCover';

/**
 * NodeCoverField
 *
 * - If no cover selected yet, show a button-like TextInput to pick one
 *   (unless emptyMode is "none").
 * - If a cover is present (local blob preview or persisted coverUrl), hide the input
 *   and show the preview with edit / zoom / remove overlay actions.
 *
 * @param {object} node - The node whose cover is being edited.
 * @param {function} onCoverSaved - Callback function that receives the new cover data when a cover is selected or removed.
 * @param {boolean} [readOnly=false] - Disables picking and removal.
 * @param {'placeholder'|'none'} [emptyMode='placeholder'] - Empty-state UI.
 *
 * @returns {ReactNode} NodeCoverField.
 */
const NodeCoverField = forwardRef(({
  node,
  onCoverSaved,
  readOnly = false,
  emptyMode = 'placeholder',
}, ref) => {

  // ----- STATE ----- //

  // Preview URL — local blob while uploading; null when cleared/removed.
  const [previewURL, setPreviewURL] = useState(null);
  // Optimistic hide so remove feels instant before parent/node catch up.
  const [coverHidden, setCoverHidden] = useState(false);

  // ----- REFS ----- //

  // Hidden file input ref

  const fileInputRef = useRef(null);

  // ----- MEMOS ----- //

  /**
   * Clear the current preview URL, revoking the object URL if it's a blob URL.
   */

  const clearPreview = useCallback((next = null) => {
    setPreviewURL(prev => {
      if (prev) revokeObjectUrl(prev);
      return next;
    });
  }, []);

  /**
   * Opens the hidden file picker when editable.
   */
  const openPicker = useCallback(() => {
    if (readOnly) return;
    fileInputRef.current?.click();
  }, [readOnly]);

  useImperativeHandle(ref, () => ({
    openPicker,
  }), [openPicker]);

  // ----- EFFECTS ----- //

  /**
   * Clear the preview when the node changes to avoid showing a preview from a previously
   * edited node.
   */

  useEffect(() => {
    clearPreview();
    setCoverHidden(false);
  }, [node?.key, clearPreview]);

  /**
   * If the node's coverUrl changes to a non-blob URL, clear the local preview to show
   * the persisted cover.
   */
  useEffect(() => {
    const url = node?.coverUrl;
    if (url && typeof url === 'string' && !isBlobUrl(url)) {
      clearPreview();
      setCoverHidden(false);
    }
    if (!url) {
      setCoverHidden(false);
    }
  }, [node?.coverUrl, clearPreview]);

  /**
   * Revoke the preview URL when the component unmounts to free up memory, if it's a blob URL.
   */
  useEffect(() => {
    return () => {
      if (previewURL) revokeObjectUrl(previewURL);
    };
  }, [previewURL]);

  // Prefer local preview first; otherwise show persisted coverUrl if present
  const displaySrc = coverHidden
    ? null
    : (
      previewURL
      || (typeof node?.coverUrl === 'string' && node.coverUrl ? node.coverUrl : null)
    );

  const showEmptyPlaceholder = !readOnly && !displaySrc && emptyMode === 'placeholder';
  const hideEmptyChrome = !displaySrc && emptyMode === 'none';
  const canPickEmpty = !displaySrc && !readOnly;

  // ----- HANDLERS ----- //

  /**
   * Handle click on the empty placeholder to trigger the hidden file input click.
   */
  const handleClick = () => {
    openPicker();
  };

  /**
   * Open the cover file picker from the edit overlay action.
   *
   * @param {React.MouseEvent} e - Click event.
   * @returns {void}
   */
  const handleEditCover = (e) => {
    e.stopPropagation();
    openPicker();
  };

  /**
   * Open the cover photo in a new browser tab for zooming.
   *
   * @param {React.MouseEvent} e - Click event.
   * @returns {Promise<void>}
   */
  const handleZoomCover = async (e) => {
    e.stopPropagation();
    if (!displaySrc) return;

    if (isBlobUrl(displaySrc) || displaySrc.startsWith('data:')) {
      window.open(displaySrc, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const url = await resolveImageUrl({
        nodeKey: node?.key,
        url: displaySrc,
        storagePath: typeof node?.coverStoragePath === 'string'
          ? node.coverStoragePath
          : undefined,
      });
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
    } catch {
      // Fall through to the raw URL.
    }

    window.open(displaySrc, '_blank', 'noopener,noreferrer');
  };

  /**
   * Handle file selection from the file input. Creates a local preview URL for the selected image
   * and calls the onCoverSaved callback with the new cover data.
   */
  const handleCoverSelected = (e) => {
    if (readOnly) return;

    // Get the selected file from the file input
    const file = e.target?.files?.[0];

    // If no file was selected, do nothing
    if (!file) return;

    // Create a local preview URL for the selected file
    const url = createObjectUrl(file);
    setCoverHidden(false);
    clearPreview(url);

    // Notify parent of new cover
    if (typeof onCoverSaved === 'function') {
      onCoverSaved({
        blob: file,
        objectURL: url,
        name: file.name,
        type: file.type,
        size: file.size,
      });
    }

    // Allow re-selecting the same file later
    if (e.target) e.target.value = '';
  };

  /**
   * Handle removing the cover by clearing the preview and calling the onCoverSaved
   * callback with a removed flag.
   *
   * @param {React.MouseEvent} e - Click event.
   * @returns {void}
   */
  const handleRemoveCover = (e) => {
    e.stopPropagation();
    if (readOnly) return;

    // Hide immediately (don't wait on server / parent patch).
    setCoverHidden(true);
    clearPreview();

    // Notify parent that cover was removed
    if (typeof onCoverSaved === 'function') {
      onCoverSaved({
        removed: true,
      });
    }
  };

  // ----- RENDER ----- //

  if (hideEmptyChrome) {
    if (readOnly) return null;

    return (
      <input
        style={{ display: 'none' }}
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleCoverSelected}
      />
    );
  }

  return (
    <>
      <div
        className="node-cover-field-container"
        style={{
          cursor: canPickEmpty ? 'pointer' : 'default',
          minHeight: displaySrc ? 'auto' : (readOnly ? 0 : '200px'),
        }}
        onClick={canPickEmpty ? handleClick : undefined}
      >

        {!readOnly ? (
          <input
            style={{
              display: 'none',
            }}
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleCoverSelected}
          />
        ) : null}

        {showEmptyPlaceholder && (
          <>
            <Icon
              iconKey="image"
              color="var(--foregroundMuted)"
              width={60}
              height={60}
            />

            {/* PLACEHOLDER TEXT */}
            <div
              className="node-cover-field-placeholder-text"
            >
              Add Cover...
            </div>
          </>
        )}

        {/* PREVIEW */}
        {displaySrc ? (
          <>
            <NodeCover
              src={displaySrc}
              clickable={false}
              width="100%"
            />

            {/* ACTIONS */}
            <div className="node-cover-field-actions">
              {!readOnly ? (
                <CoverActionButton
                  iconKey="edit"
                  title="Edit cover photo..."
                  onClick={handleEditCover}
                />
              ) : null}

              <CoverActionButton
                iconKey="zoom_in"
                title="Zoom cover photo..."
                onClick={handleZoomCover}
              />

              {!readOnly ? (
                <CoverActionButton
                  iconKey="remove"
                  title="Remove cover photo..."
                  onClick={handleRemoveCover}
                />
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </>
  );
});

// Dev Tools
NodeCoverField.displayName = 'NodeCoverField';

export default NodeCoverField;
